import React from "react";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";

import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import {
  getDailyWorkTimeCap,
  updateDailyWorkTimeCap,
} from "../../../settingsSlice";

import "./DailyWorkTimeCapSection.scss";

// Valid = a positive number of hours, at most 24 (mirrors the backend validator
// `daily_work_time_cap_hours must be in (0, 24]`). Decimals are allowed.
const isValid = (raw: string): boolean => {
  const t = raw.trim();
  if (t === "") return false;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 && n <= 24;
};

export function DailyWorkTimeCapSection() {
  const dispatch = useAppDispatch();
  const cap = useAppSelector((s) => s.settings.dailyWorkTimeCap);
  const loadAPI = useAppSelector((s) => s.settings.getDailyWorkTimeCap);
  const saveAPI = useAppSelector((s) => s.settings.updateDailyWorkTimeCap);

  const [form, setForm] = React.useState<string | undefined>(undefined);

  // Load once on mount.
  React.useEffect(() => {
    dispatch(getDailyWorkTimeCap());
  }, [dispatch]);

  // Seed / reset the local form whenever server truth changes
  // (initial load and the post-save refetch).
  React.useEffect(() => {
    if (cap !== undefined) setForm(String(cap));
  }, [cap]);

  // Surface load failures.
  React.useEffect(() => {
    if (!loadAPI.loading && loadAPI.error) {
      toast.error("Failed to load the daily work-time cap. Please try again.");
    }
  }, [loadAPI.loading, loadAPI.error]);

  const invalid = form !== undefined && !isValid(form);
  const isDirty =
    form !== undefined && cap !== undefined && form.trim() !== String(cap);
  const canSave =
    form !== undefined && isValid(form) && isDirty && !saveAPI.loading;

  const onSave = async () => {
    if (form === undefined || !canSave) return;
    const result = await dispatch(updateDailyWorkTimeCap(Number(form.trim())));
    if (updateDailyWorkTimeCap.fulfilled.match(result)) {
      toast.success("Daily work-time cap saved.");
    } else {
      toast.error("Failed to save the daily work-time cap. Please try again.");
    }
  };

  const heading = (
    <div className="section-heading">
      <h3>Daily Work-Time Cap</h3>
      <span className="section-hint">
        Maximum hours a driver may be on the clock (driving + on-site service) in a
        single route. The optimizer treats a longer route as infeasible, so work
        spreads across the fleet instead of piling onto one driver.
      </span>
    </div>
  );

  // Initial load spinner (before we have a value to show).
  if (loadAPI.loading && form === undefined) {
    return (
      <section className="ro-section daily-work-time-cap-section">
        {heading}
        <div className="section-loading">
          <Oval color="#0A60BB" height={28} width={28} secondaryColor="#DAE0E6" />
        </div>
      </section>
    );
  }

  return (
    <section className="ro-section daily-work-time-cap-section">
      {heading}

      <div className={`field-container${invalid ? " has-error" : ""}`}>
        <label className="label" htmlFor="daily-work-time-cap">
          Max hours per route
        </label>
        <div className="minutes-input">
          <input
            id="daily-work-time-cap"
            type="number"
            min={0}
            max={24}
            step={0.5}
            value={form ?? ""}
            aria-label="Max hours per route"
            aria-invalid={invalid}
            onChange={(e) => setForm(e.target.value)}
          />
          <span className="suffix">hours</span>
        </div>
        {invalid && (
          <span className="form-error">
            Enter a number of hours greater than 0 and at most 24.
          </span>
        )}
      </div>

      <div className="ro-footer">
        <button
          type="button"
          className="submit-button"
          onClick={onSave}
          disabled={!canSave}
        >
          {saveAPI.loading ? "Saving…" : "Save changes"}
        </button>
      </div>
    </section>
  );
}
